/**
 * useBackgroundUploader Hook
 *
 * A top-level hook that initializes and manages the background upload system.
 *
 * Responsibilities:
 * - Sync online/offline status to upload manager
 * - Sync project ID to upload manager
 * - Start pending uploads when on WorkerStatus page and online
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'modules/state-mgmt/store';
import * as uploadManager from 'utils/uploadManager';
import { ROUTES } from 'constants/routes';

const useBackgroundUploader = () => {
  const location = useLocation();
  const { isOnline, projectId, isWorker, languageCode } = useAppSelector(state => ({
    isOnline: state.app.isOnline,
    projectId: state.cache.project?.id,
    isWorker: state.user?.isWorker,
    languageCode: state.app.languageCode,
  }));

  // Sync online status and start pending uploads when on WorkerStatus page
  useEffect(() => {
    const syncAndStart = async () => {
      // Sync online status (await in case pauseAll runs when going offline)
      await uploadManager.setOnline(isOnline);

      // Start pending uploads if conditions are met
      const isOnWorkerStatusPage = location.pathname === ROUTES.WORKER_STATUS.path;
      if (isOnWorkerStatusPage && isOnline && projectId) {
        uploadManager.startPendingUploads(projectId);
      }
    };

    syncAndStart();
  }, [isOnline, projectId, location.pathname]);

  // Sync project ID to upload manager
  useEffect(() => {
    if (projectId) {
      uploadManager.setProjectId(projectId);
    }
  }, [projectId]);

  // Sync language code to upload manager
  useEffect(() => {
    if (languageCode) {
      uploadManager.setLanguageCode(languageCode);
    }
  }, [languageCode]);

  return null;
};

export default useBackgroundUploader;
